/**
 * «Extracto en dos toques»: lógica pura, sin React. Qué hacer con el archivo
 * que llega al compartir desde la app del banco, cómo recordar a qué cuenta
 * va y cuál es la única duda que Gavi pregunta antes de subirlo a bordo.
 */
import {
  extension,
  seTrae,
  tipoArchivo,
} from '#components/mobile/importar/importar';
import type {
  FilaVista,
  MovimientoAImportar,
} from '#components/mobile/importar/importar';
import type { ArchivoLeido } from '#components/mobile/importar/leerArchivo';

/** Lo que se puede hacer con el archivo compartido. */
export type QueHacer = 'leer' | 'pdf' | 'formato';

export function queHacer(nombre: string): QueHacer {
  if (extension(nombre) === 'pdf') {
    return 'pdf';
  }
  return tipoArchivo(nombre) ? 'leer' : 'formato';
}

/**
 * Huella del archivo para reconocer de qué cuenta es la próxima vez: el tipo
 * y, en CSV y Excel, la fila de cabecera (cada banco tiene la suya). En Norma
 * 43, OFX o QIF basta el formato.
 */
export function firmaArchivo(nombre: string, leido: ArchivoLeido): string {
  const ext = extension(nombre);
  if (leido.tipo !== 'tabla') {
    return `estructurado:${ext}`;
  }
  const fila = leido.lectura?.filaCabecera;
  const cabecera =
    fila != null
      ? (leido.filas[fila] ?? [])
          .map(c => c.trim().toLowerCase())
          .filter(Boolean)
          .join('|')
      : '';
  return `tabla:${ext}:${cabecera}`;
}

/** Huella → id de cuenta (pref sincronizada `compartir-cuentas`). */
export type Firmas = Record<string, string>;

export function leerFirmas(raw: string | undefined | null): Firmas {
  if (!raw) {
    return {};
  }
  try {
    const datos: unknown = JSON.parse(raw);
    if (!datos || typeof datos !== 'object' || Array.isArray(datos)) {
      return {};
    }
    return Object.fromEntries(
      Object.entries(datos).filter(
        (e): e is [string, string] => typeof e[1] === 'string',
      ),
    );
  } catch {
    return {};
  }
}

export function guardarFirma(
  raw: string | undefined | null,
  firma: string,
  cuenta: string,
): string {
  return JSON.stringify({ ...leerFirmas(raw), [firma]: cuenta });
}

/**
 * ¿A qué cuenta va sin preguntar? Si solo hay una abierta, a esa; si este
 * archivo ya se subió antes a una cuenta que sigue abierta, a esa. Si no,
 * null: Gavi pregunta.
 */
export function cuentaSinPreguntar(
  firmas: Firmas,
  firma: string,
  abiertas: string[],
): string | null {
  if (abiertas.length === 1) {
    return abiertas[0];
  }
  const recordada = firmas[firma];
  return recordada && abiertas.includes(recordada) ? recordada : null;
}

/** El comercio por el que pregunta Gavi y los movimientos que lo llevan. */
export type Duda = { nombre: string; ids: string[] };

/**
 * La única duda: entre lo nuevo sin categoría, el comercio que Rumbo no
 * conoce y más se repite. Lo que ya conoce lo coloca solo (o queda en «Por
 * revisar» como siempre).
 */
export function dudaComercio(
  filas: FilaVista[],
  conocidos: Set<string>,
): Duda | null {
  const porNombre = new Map<string, string[]>();
  for (const f of filas) {
    if (!seTrae(f) || f.categoria != null) {
      continue;
    }
    const nombre = f.nombre.trim();
    if (!nombre || conocidos.has(nombre.toLowerCase())) {
      continue;
    }
    porNombre.set(nombre, [...(porNombre.get(nombre) ?? []), f.id]);
  }
  let duda: Duda | null = null;
  for (const [nombre, ids] of porNombre) {
    if (!duda || ids.length > duda.ids.length) {
      duda = { nombre, ids };
    }
  }
  return duda;
}

/**
 * Las categorías que se ofrecen como respuesta: las más repetidas entre lo
 * nuevo que ya trae categoría y, si faltan, las últimas que se eligieron
 * (como mucho `max`). El resto, en «Otra…».
 */
export function categoriasSugeridas(
  filas: FilaVista[],
  recientes: string[] = [],
  max = 3,
): string[] {
  const cuenta = new Map<string, number>();
  for (const f of filas) {
    if (seTrae(f) && f.categoria) {
      cuenta.set(f.categoria, (cuenta.get(f.categoria) ?? 0) + 1);
    }
  }
  const delExtracto = [...cuenta.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([id]) => id);
  return [...new Set([...delExtracto, ...recientes])].slice(0, max);
}

/**
 * Pone la categoría elegida a los movimientos del comercio de la duda antes
 * de importarlos. Solo a lo que entra ahora: lo que ya estaba no se toca.
 */
export function conCategoriaElegida(
  todos: MovimientoAImportar[],
  duda: Duda | null,
  categoria: string | null,
): MovimientoAImportar[] {
  if (!duda || !categoria) {
    return todos;
  }
  const ids = new Set(duda.ids);
  return todos.map(m =>
    ids.has(m.trx_id) ? { ...m, category: categoria } : m,
  );
}
